class FacetDrawerFiltersForm extends HTMLElement {
  connectedCallback() {
    this._debouncedApply = this._debounce(this._applyFilters.bind(this), 500);
    this._body = this.querySelector('.facet-drawer__body');
    this.addEventListener('click', this._handlePillClick.bind(this), true);
    this.addEventListener('change', this._handleChange.bind(this));
    this.addEventListener('input', this._handleInput.bind(this));
    // Capture phase: runs before FacetRemove.closeFilter which looks for a facet-filters-form
    // that doesn't exist inside the drawer — we handle facet-remove links ourselves.
    this.addEventListener('click', this._handleFacetRemove.bind(this), true);
    this.addEventListener('click', this._handleClick.bind(this));
    this.addEventListener('keydown', this._handleKeydown.bind(this));
  }

  _handlePillClick(event) {
    const label = event.target.closest('label.facet-drawer__pill');
    if (!label) return;

    const input = label.control || document.getElementById(label.htmlFor);
    if (!input?.matches('.facet-drawer__checkbox, .facet-drawer__radio')) return;

    event.preventDefault();
    event.stopPropagation();

    const scrollTop = this._body?.scrollTop ?? 0;
    let changed = false;

    if (input.type === 'checkbox') {
      input.checked = !input.checked;
      changed = true;
    } else if (input.type === 'radio' && !input.checked) {
      input.checked = true;
      changed = true;
    }

    if (!changed) return;

    input.dispatchEvent(new Event('change', { bubbles: true }));
    this._restoreBodyScroll(scrollTop);
  }

  _restoreBodyScroll(scrollTop) {
    if (!this._body || scrollTop == null) return;
    this._body.scrollTop = scrollTop;
  }

  _handleFacetRemove(event) {
    const link = event.target.closest('facet-remove a');
    if (!link) return;
    event.stopImmediatePropagation();
    event.preventDefault();
    const href = link.getAttribute('href') || '';
    const queryIdx = href.indexOf('?');
    const searchParams = queryIdx === -1 ? '' : href.slice(queryIdx + 1);
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.renderPage(searchParams, event);
    }
    this._syncBadgeFromParams(new URLSearchParams(searchParams));
  }

  _handleChange(event) {
    if (event.target.type === 'checkbox' || event.target.type === 'radio') {
      this._applyFilters(event);
    }
  }

  _handleInput(event) {
    if (event.target.type === 'text' || event.target.type === 'number') {
      this._debouncedApply(event);
    }
  }

  _handleClick(event) {
    if (event.target.closest('.facet-drawer__close-btn')) {
      this.closeDrawer();
    }
  }

  _handleKeydown(event) {
    if (event.code !== 'Enter' && event.code !== 'Space') return;
    if (event.target.closest('.facet-drawer__close-btn')) {
      event.preventDefault();
      this.closeDrawer();
    }
  }

  closeDrawer() {
    const summary = this.closest('details')?.querySelector(':scope > summary');
    if (summary) summary.click();
  }

  _applyFilters(event) {
    const form = this.querySelector('form');
    if (!form) return;
    const scrollTop = this._body?.scrollTop ?? null;
    const params = new URLSearchParams(new FormData(form));

    // Remove price params that are at their default values so they don't create unintentional filters
    const slider = form.querySelector('price-range-slider');
    if (slider) {
      const maxPrice = parseFloat(slider.dataset.max);
      const minInput = document.getElementById(slider.dataset.minInput);
      const maxInput = document.getElementById(slider.dataset.maxInput);
      if (minInput && !(parseFloat(minInput.value) > 0)) params.delete(minInput.name);
      if (maxInput && !isNaN(maxPrice) && !(parseFloat(maxInput.value) < maxPrice)) params.delete(maxInput.name);
    }

    const searchParams = params.toString();
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.renderPage(searchParams, event, true, null, scrollTop);
    }
    this._syncBadgeFromForm(form);
    this._restoreBodyScroll(scrollTop);
    requestAnimationFrame(() => this._restoreBodyScroll(scrollTop));
  }

  _syncBadgeFromForm(form) {
    let count = form.querySelectorAll('.facet-drawer__checkbox:checked').length;

    // Price range: active only when min > 0 or max < range_max
    const slider = form.querySelector('price-range-slider');
    if (slider) {
      const maxPrice = parseFloat(slider.dataset.max);
      const minInput = document.getElementById(slider.dataset.minInput);
      const maxInput = document.getElementById(slider.dataset.maxInput);
      const minVal = parseFloat(minInput?.value) || 0;
      const maxVal = parseFloat(maxInput?.value);
      if (minVal > 0 || (!isNaN(maxVal) && !isNaN(maxPrice) && maxVal < maxPrice)) {
        count += 1;
      }
    }

    this._setBadge(count);
  }

  _syncBadgeFromParams(params) {
    let count = 0;
    let hasPriceFilter = false;
    for (const [key, value] of params) {
      if (!key.startsWith('filter.') || !value) continue;
      if (key.includes('price.gte') || key.includes('price.lte')) {
        hasPriceFilter = true;
      } else {
        count++;
      }
    }
    if (hasPriceFilter) count++;
    this._setBadge(count);
  }

  _setBadge(count) {
    const button = document.querySelector('.mobile-facets__open');
    if (!button) return;
    let badge = button.querySelector('.facet-count-badge');
    if (count > 0) {
      if (badge) {
        badge.textContent = count;
      } else {
        badge = document.createElement('span');
        badge.className = 'facet-count-badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = count;
        button.appendChild(badge);
      }
    } else if (badge) {
      badge.remove();
    }
  }

  _debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('facet-drawer-filters-form', FacetDrawerFiltersForm);

class PriceRangeSlider extends HTMLElement {
  connectedCallback() {
    const parsed = parseFloat(this.dataset.max);
    this._max = !isNaN(parsed) && parsed > 0 ? parsed : 100;
    this._minInput = document.getElementById(this.dataset.minInput);
    this._maxInput = document.getElementById(this.dataset.maxInput);

    const ranges = this.querySelectorAll('input[type="range"]');
    this._minRange = ranges[0];
    this._maxRange = ranges[1];
    this._fill = this.querySelector('.price-drawer__fill');

    // Init range sliders from their own HTML value attributes (set by Liquid),
    // then sync text inputs to match. Avoids DOM timing issues where text
    // input values aren't settled when connectedCallback fires.
    const minVal = parseFloat(this._minRange.value);
    const maxVal = parseFloat(this._maxRange.value);
    if (this._minInput && !isNaN(minVal)) this._minInput.value = minVal;
    if (this._maxInput && !isNaN(maxVal)) this._maxInput.value = maxVal;
    this._updateFill();

    this._minRange.addEventListener('input', () => this._onMinRange());
    this._maxRange.addEventListener('input', () => this._onMaxRange());

    if (this._minInput) this._bindFocusState(this._minInput);
    if (this._maxInput) this._bindFocusState(this._maxInput);

    if (this._minInput) this._minInput.addEventListener('change', () => this._updateRangeFromInputs());
    if (this._maxInput) this._maxInput.addEventListener('change', () => this._updateRangeFromInputs());
  }

  _onMinRange() {
    let val = parseFloat(this._minRange.value);
    const maxVal = parseFloat(this._maxRange.value);
    if (val >= maxVal) {
      val = maxVal;
      this._minRange.value = val;
    }
    if (this._minInput) {
      this._minInput.value = val;
      this._minInput.dispatchEvent(new Event('input', { bubbles: true }));
      this._minInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this._updateFill();
  }

  _onMaxRange() {
    let val = parseFloat(this._maxRange.value);
    const minVal = parseFloat(this._minRange.value);
    if (val <= minVal) {
      val = minVal;
      this._maxRange.value = val;
    }
    if (this._maxInput) {
      this._maxInput.value = val;
      this._maxInput.dispatchEvent(new Event('input', { bubbles: true }));
      this._maxInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this._updateFill();
  }

  _updateRangeFromInputs() {
    if (this._minInput) {
      const v = parseFloat(this._minInput.value);
      this._minRange.value = isNaN(v) ? 0 : v;
    }
    if (this._maxInput) {
      const v = parseFloat(this._maxInput.value);
      this._maxRange.value = isNaN(v) ? this._max : v;
    }
    this._updateFill();
  }

  _bindFocusState(input) {
    const wrapper = input.closest('.price-drawer__input-wrap');
    if (!wrapper) return;

    input.addEventListener('focus', () => {
      wrapper.classList.add('is-focused');
    });

    input.addEventListener('blur', () => {
      wrapper.classList.remove('is-focused');
    });
  }

  _updateFill() {
    if (!this._fill) return;
    const min = parseFloat(this._minRange.value) || 0;
    const max = parseFloat(this._maxRange.value) || this._max;
    this._fill.style.left = `${(min / this._max) * 100}%`;
    this._fill.style.right = `${100 - (max / this._max) * 100}%`;
  }
}

customElements.define('price-range-slider', PriceRangeSlider);
